import React, { useMemo, useState } from "react";
import { ArrowSquareOut, BookOpenText, Eye, EyeSlash, Lock } from "@phosphor-icons/react";

/**
 * Render a session's papers/resources block.
 *
 * Two display modes:
 *  - Delegate (default): only shows items where `released === true`.
 *  - Admin (`isAdmin`):  shows every parsed item with a per-item "Release / Hide"
 *                        toggle so an organiser can drip-feed the list during
 *                        the live talk. Also exposes a "Release all" /
 *                        "Hide all" pair for one-tap control.
 *
 * Props:
 *   items        - preferred. Array of {id, text, released} returned by the
 *                  backend (`resources_items`).
 *   resources    - fallback. Raw text. If provided without items, every line
 *                  is rendered (used for back-compat & in the admin edit
 *                  preview).
 *   isAdmin      - show toggle UI.
 *   onToggle     - (resourceId) => Promise - flip one item.
 *   onReleaseAll - () => Promise.
 *   onHideAll    - () => Promise.
 */
export function ResourcesBlock({
  items,
  resources,
  isAdmin = false,
  onToggle,
  onReleaseAll,
  onHideAll,
  testid = "session-resources",
}) {
  const parsed = useMemo(() => {
    if (Array.isArray(items)) return items;
    // Fallback: derive a visible-by-default list from plain text.
    return splitCitations(resources || "").map((text, i) => ({
      id: `legacy-${i}`,
      text,
      released: true,
    }));
  }, [items, resources]);

  const visible = isAdmin ? parsed : parsed.filter((it) => it.released);
  const releasedCount = parsed.filter((it) => it.released).length;
  const total = parsed.length;

  if (total === 0) return null;
  // Nothing released yet and delegate view: render nothing (clean UI during talks).
  if (!isAdmin && visible.length === 0) return null;

  return (
    <div className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BookOpenText size={18} weight="duotone" className="text-[#000000]" />
          <h2 className="font-display text-lg">Papers &amp; resources</h2>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C]"
              data-testid={`${testid}-released-count`}
            >
              {releasedCount}/{total} released
            </span>
            {releasedCount < total && (
              <button
                type="button"
                onClick={onReleaseAll}
                className="text-xs font-medium px-2 py-1 rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
                data-testid={`${testid}-release-all`}
              >
                Release all
              </button>
            )}
            {releasedCount > 0 && (
              <button
                type="button"
                onClick={onHideAll}
                className="text-xs font-medium px-2 py-1 rounded-sm border border-[#E5E9F0] text-[#1c1c24] hover:border-[#000000]"
                data-testid={`${testid}-hide-all`}
              >
                Hide all
              </button>
            )}
          </div>
        )}
      </div>

      <ol className="space-y-3" data-testid={`${testid}-list`}>
        {visible.map((it, i) => {
          const isReleased = it.released;
          return (
            <li
              key={it.id}
              className={`text-sm leading-relaxed ${
                isAdmin && !isReleased ? "opacity-60" : ""
              }`}
              data-testid={`${testid}-item-${it.id}`}
            >
              <div className="flex gap-2 items-start">
                <span className="font-mono-time text-xs text-[#7A828C] flex-shrink-0 mt-0.5 w-6">
                  {i + 1}.
                </span>
                <div className="min-w-0 flex-1 whitespace-pre-line break-words text-[#1c1c24]">
                  {renderLinks(it.text)}
                </div>
                {isAdmin && (
                  <ReleaseToggle
                    released={isReleased}
                    onClick={() => onToggle?.(it.id)}
                    testid={`${testid}-toggle-${it.id}`}
                  />
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {isAdmin && releasedCount === 0 && (
        <p className="mt-3 text-xs text-[#7A828C]" data-testid={`${testid}-hint`}>
          <Lock size={11} weight="duotone" className="inline mr-1 -mt-0.5" />
          Hidden from delegates until you release each paper. Releasing also pushes a notification.
        </p>
      )}
    </div>
  );
}

function ReleaseToggle({ released, onClick, testid }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onClick?.();
    } finally {
      setBusy(false);
    }
  };
  if (released) {
    return (
      <button
        type="button"
        onClick={handle}
        disabled={busy}
        className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono-time px-2 py-1 rounded-sm bg-[#E8F5EC] text-[#1b6b34] hover:bg-[#cfe9d6] disabled:opacity-50"
        data-testid={testid}
      >
        <Eye size={11} weight="duotone" /> Released
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy}
      className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono-time px-2 py-1 rounded-sm border border-[#E5E9F0] text-[#5a5a5a] hover:border-[#000000] hover:text-[#000000] disabled:opacity-50"
      data-testid={testid}
    >
      <EyeSlash size={11} /> Release
    </button>
  );
}

/**
 * Split free-text resources into individual citations. Blank lines separate
 * citations; single newlines within a citation are preserved.
 */
function splitCitations(raw) {
  return raw
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const URL_RX = /(https?:\/\/[^\s<>"']+)/gi;
const DOI_RX = /(?:doi:\s*)(10\.\d{4,9}\/[-._;()/:A-Z0-9]+)/gi;
const PMID_RX = /\bPMID:\s*(\d{4,})/gi;
const PMCID_RX = /\bPMCID:\s*(PMC\d+)/gi;

function renderLinks(text) {
  // Build an ordered list of matches across all patterns, then weave the text + <a> tags.
  const matches = [];
  const collect = (rx, makeUrl, label) => {
    let m;
    rx.lastIndex = 0;
    while ((m = rx.exec(text)) !== null) {
      matches.push({ start: m.index, end: m.index + m[0].length, url: makeUrl(m), label: label(m) });
    }
  };
  collect(URL_RX, (m) => m[0], (m) => m[0]);
  collect(DOI_RX, (m) => `https://doi.org/${m[1]}`, (m) => m[0]);
  collect(PMID_RX, (m) => `https://pubmed.ncbi.nlm.nih.gov/${m[1]}/`, (m) => m[0]);
  collect(PMCID_RX, (m) => `https://www.ncbi.nlm.nih.gov/pmc/articles/${m[1]}/`, (m) => m[0]);
  matches.sort((a, b) => a.start - b.start);

  // De-overlap: drop any later match that starts before the previous one ended.
  const dedup = [];
  let lastEnd = -1;
  for (const m of matches) {
    if (m.start >= lastEnd) {
      dedup.push(m);
      lastEnd = m.end;
    }
  }

  if (dedup.length === 0) return text;

  const out = [];
  let cursor = 0;
  dedup.forEach((m, i) => {
    if (m.start > cursor) out.push(text.slice(cursor, m.start));
    out.push(
      <a
        key={`l${i}`}
        href={m.url}
        target="_blank"
        rel="noreferrer noopener"
        className="text-[#000000] hover:underline inline-flex items-center gap-0.5"
      >
        {m.label}
        <ArrowSquareOut size={11} className="inline" />
      </a>
    );
    cursor = m.end;
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}
